import { msgs } from "../define";

export const setup = msgs({
  en: {
    "setup.title": "Set up {salon}",
    "setup.progress": "Step {n} of {total}",
    "setup.exit": "Sign out",
    "setup.step.profile": "Salon profile",
    "setup.step.branch": "Location & hours",
    "setup.step.services": "Services",
    "setup.step.team": "Team",
    "setup.step.payments": "Payments",
    "setup.step.review": "Review",
    "setup.optional": "Optional",
    "setup.verifyBanner": "We sent a confirmation link to {email}. Confirming your email is required to use Senvia POS.",
    "setup.profile.title": "Tell us about your salon",
    "setup.profile.sub": "Picking a country fills in currency, time zone, number format and tax. Everything stays editable.",
    "setup.profile.name": "Salon name",
    "setup.profile.logo": "Logo",
    "setup.profile.logo.hint": "PNG, JPEG or WebP, up to 256 KB.",
    "setup.profile.logo.upload": "Upload logo",
    "setup.profile.logo.replace": "Replace",
    "setup.profile.logo.tooBig": "The logo must be smaller than 256 KB.",
    "setup.profile.country": "Country",
    "setup.profile.currency": "Currency",
    "setup.profile.timezone": "Time zone",
    "setup.profile.uiLocale": "App language",
    "setup.profile.formatLocale": "Number & date format",
    "setup.profile.formatExample": "Example: {example}",
    "setup.profile.phone": "Salon phone",
    "setup.profile.phone.hint": "Country code +{code}",
    "setup.profile.tax": "Tax",
    "setup.profile.taxLabel": "Tax name",
    "setup.profile.taxRate": "Rate (%)",
    "setup.profile.taxMode": "Prices",
    "setup.profile.taxInclusive": "Prices include tax",
    "setup.profile.taxExclusive": "Tax added at checkout",
    "setup.profile.taxHint": "Only charge tax if your business is registered for it. Set 0% if not.",
    "setup.profile.defaultsApplied": "Defaults for {country} applied.",
    "setup.branch.title": "Your location and opening hours",
    "setup.branch.sub": "Customers can only be booked inside opening hours. You can add more branches later in Settings.",
    "setup.branch.name": "Location name",
    "setup.branch.name.ph": "e.g. Main branch",
    "setup.branch.address": "Address",
    "setup.branch.phone": "Phone",
    "setup.branch.hours": "Opening hours",
    "setup.branch.open": "Open",
    "setup.branch.closed": "Closed",
    "setup.branch.copyAll": "Copy Monday to all open days",
    "setup.branch.to": "to",
    "setup.branch.invalid": "Closing time must be after opening time.",
    "setup.services.title": "Add your services",
    "setup.services.sub": "Start from a template and adjust prices — they're shown in {currency}.",
    "setup.services.templates": "Quick start templates",
    "setup.services.addTemplate": "Add {category}",
    "setup.services.added": "{n} services added",
    "setup.services.empty": "No services yet. Pick a template above or add your own.",
    "setup.services.name": "Service",
    "setup.services.duration": "Minutes",
    "setup.services.price": "Price ({currency})",
    "setup.services.from": "From",
    "setup.services.addRow": "Add a service",
    "setup.services.count": "{n} services",
    "setup.team.title": "Add your team",
    "setup.team.sub": "Add the people who take appointments or use the till. Email is optional — without one they use the kiosk PIN only.",
    "setup.team.you": "You",
    "setup.team.bookable": "Takes appointments",
    "setup.team.pin": "Staff PIN",
    "setup.team.pin.hint": "4–6 digits to switch staff on the shared counter tablet and approve at checkout.",
    "setup.team.pinSet": "PIN set",
    "setup.team.noPin": "No PIN",
    "setup.team.role": "Role",
    "setup.team.email.hint": "We'll email an invitation to set a password.",
    "setup.team.add": "Add team member",
    "setup.team.invited": "Invitation sent",
    "setup.team.noLogin": "Kiosk only",
    "setup.team.empty": "Just you for now? That's fine — you can add people any time.",
    "setup.team.clearPin": "Remove PIN",
    "setup.payments.title": "How will customers pay?",
    "setup.payments.sub": "Cash and your existing card terminal always work. Card payments online and in-app go to your own Stripe account.",
    "setup.payments.stripe.title": "Connect your own Stripe account",
    "setup.payments.stripe.body": "Money goes straight to your Stripe balance and bank — the platform never holds your funds. Stripe charges its own processing fees.",
    "setup.payments.stripe.cta": "I'll connect Stripe",
    "setup.payments.stripe.tutorial": "How to create and connect a Stripe account",
    "setup.payments.stripe.later": "Connection opens in Settings → Payments once available. Until then you can take cash and terminal payments.",
    "setup.payments.manual.title": "Cash & manual only for now",
    "setup.payments.manual.body": "Record cash, your own card terminal, bank transfer or e-wallet as manual payments. Connect Stripe any time later.",
    "setup.payments.manual.cta": "Use cash & manual",
    "setup.payments.chosen": "Selected",
    "setup.review.title": "Ready to open your salon",
    "setup.review.sub": "Check the summary. You can change everything later in Settings.",
    "setup.review.profile": "{currency} · {timezone} · {tax}",
    "setup.review.hours": "{days} days a week",
    "setup.review.noHours": "No opening hours",
    "setup.review.services": "{n} services",
    "setup.review.team": "{n} people",
    "setup.review.payments.manual": "Cash & manual payments",
    "setup.review.payments.stripe": "Stripe (connect in Settings)",
    "setup.review.payments.undecided": "Not chosen yet",
    "setup.review.skipped": "Skipped",
    "setup.review.missing": "Required",
    "setup.review.verifyFirst": "Confirm your email ({email}) to continue.",
    "setup.review.resend": "Resend confirmation email",
    "setup.review.checkAgain": "I've confirmed it",
    "setup.review.plan": "Base plan · USD 20/month · trial",
    "setup.review.finish": "Go to dashboard",
    "setup.review.finishing": "Opening your salon…",
    "setup.done.toast": "Your salon is ready. Welcome!",
    "setup.error.setup_incomplete": "Please complete the required steps first.",
    "setup.error.email_unverified": "Confirm your email address to continue.",
    "setup.error.currency_locked": "Currency can't change after sales were recorded.",
    "setup.notOwner": "Your salon is still being set up by the owner.",
  },
  zh: {
    "setup.title": "设置 {salon}",
    "setup.progress": "第 {n} 步，共 {total} 步",
    "setup.exit": "退出登录",
    "setup.step.profile": "店铺资料",
    "setup.step.branch": "门店与营业时间",
    "setup.step.services": "服务项目",
    "setup.step.team": "团队",
    "setup.step.payments": "收款方式",
    "setup.step.review": "确认",
    "setup.optional": "可选",
    "setup.verifyBanner": "我们已向 {email} 发送确认链接，必须先确认邮箱才能使用 Senvia POS。",
    "setup.profile.title": "介绍一下您的店铺",
    "setup.profile.sub": "选择国家/地区后会自动填写货币、时区、数字格式和税务设置，所有内容均可修改。",
    "setup.profile.name": "店铺名称",
    "setup.profile.logo": "标志",
    "setup.profile.logo.hint": "PNG、JPEG 或 WebP，不超过 256 KB。",
    "setup.profile.logo.upload": "上传标志",
    "setup.profile.logo.replace": "更换",
    "setup.profile.logo.tooBig": "标志图片必须小于 256 KB。",
    "setup.profile.country": "国家/地区",
    "setup.profile.currency": "货币",
    "setup.profile.timezone": "时区",
    "setup.profile.uiLocale": "应用语言",
    "setup.profile.formatLocale": "数字与日期格式",
    "setup.profile.formatExample": "示例：{example}",
    "setup.profile.phone": "店铺电话",
    "setup.profile.phone.hint": "国家代码 +{code}",
    "setup.profile.tax": "税务",
    "setup.profile.taxLabel": "税种名称",
    "setup.profile.taxRate": "税率（%）",
    "setup.profile.taxMode": "价格",
    "setup.profile.taxInclusive": "价格已含税",
    "setup.profile.taxExclusive": "结账时另加税",
    "setup.profile.taxHint": "仅在您的企业已登记纳税时收税，否则请设为 0%。",
    "setup.profile.defaultsApplied": "已应用 {country} 的默认设置。",
    "setup.branch.title": "门店地址与营业时间",
    "setup.branch.sub": "只能在营业时间内接受预约。之后可在设置中添加更多分店。",
    "setup.branch.name": "门店名称",
    "setup.branch.name.ph": "例如：总店",
    "setup.branch.address": "地址",
    "setup.branch.phone": "电话",
    "setup.branch.hours": "营业时间",
    "setup.branch.open": "营业",
    "setup.branch.closed": "休息",
    "setup.branch.copyAll": "将周一时间复制到所有营业日",
    "setup.branch.to": "至",
    "setup.branch.invalid": "打烊时间必须晚于开门时间。",
    "setup.services.title": "添加服务项目",
    "setup.services.sub": "从模板开始并调整价格——价格以 {currency} 显示。",
    "setup.services.templates": "快速模板",
    "setup.services.addTemplate": "添加{category}",
    "setup.services.added": "已添加 {n} 个服务项目",
    "setup.services.empty": "还没有服务项目。请选择上方模板或自行添加。",
    "setup.services.name": "服务项目",
    "setup.services.duration": "分钟",
    "setup.services.price": "价格（{currency}）",
    "setup.services.from": "起价",
    "setup.services.addRow": "添加服务项目",
    "setup.services.count": "{n} 个服务项目",
    "setup.team.title": "添加团队成员",
    "setup.team.sub": "添加负责预约或使用收银的人员。邮箱可选——没有邮箱的成员只能使用自助终端 PIN。",
    "setup.team.you": "您",
    "setup.team.bookable": "接受预约",
    "setup.team.pin": "员工 PIN",
    "setup.team.pin.hint": "4–6 位数字，用于在共用收银平板上切换员工和收银审批。",
    "setup.team.pinSet": "已设置 PIN",
    "setup.team.noPin": "未设置 PIN",
    "setup.team.role": "角色",
    "setup.team.email.hint": "我们会发送邀请邮件让其设置密码。",
    "setup.team.add": "添加团队成员",
    "setup.team.invited": "已发送邀请",
    "setup.team.noLogin": "仅限自助终端",
    "setup.team.empty": "暂时只有您一个人？没关系，随时可以添加成员。",
    "setup.team.clearPin": "移除 PIN",
    "setup.payments.title": "顾客如何付款？",
    "setup.payments.sub": "现金和您现有的刷卡机始终可用。线上及应用内的银行卡付款将直接进入您自己的 Stripe 账户。",
    "setup.payments.stripe.title": "连接您自己的 Stripe 账户",
    "setup.payments.stripe.body": "款项直接进入您的 Stripe 余额和银行账户——平台从不经手您的资金。Stripe 会收取其自身的手续费。",
    "setup.payments.stripe.cta": "我将连接 Stripe",
    "setup.payments.stripe.tutorial": "如何创建并连接 Stripe 账户",
    "setup.payments.stripe.later": "功能开放后可在 设置 → 收款 中连接。在此之前可使用现金和刷卡机收款。",
    "setup.payments.manual.title": "暂时只用现金和手动收款",
    "setup.payments.manual.body": "将现金、自有刷卡机、银行转账或电子钱包记录为手动收款。之后随时可以连接 Stripe。",
    "setup.payments.manual.cta": "使用现金和手动收款",
    "setup.payments.chosen": "已选择",
    "setup.review.title": "准备开业",
    "setup.review.sub": "请检查以下摘要，之后均可在设置中修改。",
    "setup.review.profile": "{currency} · {timezone} · {tax}",
    "setup.review.hours": "每周营业 {days} 天",
    "setup.review.noHours": "未设置营业时间",
    "setup.review.services": "{n} 个服务项目",
    "setup.review.team": "{n} 位成员",
    "setup.review.payments.manual": "现金和手动收款",
    "setup.review.payments.stripe": "Stripe（在设置中连接）",
    "setup.review.payments.undecided": "尚未选择",
    "setup.review.skipped": "已跳过",
    "setup.review.missing": "必填",
    "setup.review.verifyFirst": "请先确认您的邮箱（{email}）再继续。",
    "setup.review.resend": "重新发送确认邮件",
    "setup.review.checkAgain": "我已确认",
    "setup.review.plan": "基础版 · 每月 20 美元 · 试用中",
    "setup.review.finish": "进入控制台",
    "setup.review.finishing": "正在开启您的店铺…",
    "setup.done.toast": "店铺已就绪，欢迎！",
    "setup.error.setup_incomplete": "请先完成必填步骤。",
    "setup.error.email_unverified": "请先确认您的邮箱再继续。",
    "setup.error.currency_locked": "已有销售记录后不能更改货币。",
    "setup.notOwner": "店主仍在设置店铺。",
  },
  ms: {
    "setup.title": "Sediakan {salon}",
    "setup.progress": "Langkah {n} daripada {total}",
    "setup.exit": "Log keluar",
    "setup.step.profile": "Profil salun",
    "setup.step.branch": "Lokasi & waktu",
    "setup.step.services": "Perkhidmatan",
    "setup.step.team": "Pasukan",
    "setup.step.payments": "Bayaran",
    "setup.step.review": "Semakan",
    "setup.optional": "Pilihan",
    "setup.verifyBanner": "Kami telah menghantar pautan pengesahan ke {email}. Pengesahan e-mel diwajibkan untuk menggunakan Senvia POS.",
    "setup.profile.title": "Ceritakan tentang salun anda",
    "setup.profile.sub": "Memilih negara akan mengisi mata wang, zon waktu, format nombor dan cukai. Semuanya boleh diubah.",
    "setup.profile.name": "Nama salun",
    "setup.profile.logo": "Logo",
    "setup.profile.logo.hint": "PNG, JPEG atau WebP, sehingga 256 KB.",
    "setup.profile.logo.upload": "Muat naik logo",
    "setup.profile.logo.replace": "Ganti",
    "setup.profile.logo.tooBig": "Logo mesti kurang daripada 256 KB.",
    "setup.profile.country": "Negara",
    "setup.profile.currency": "Mata wang",
    "setup.profile.timezone": "Zon waktu",
    "setup.profile.uiLocale": "Bahasa aplikasi",
    "setup.profile.formatLocale": "Format nombor & tarikh",
    "setup.profile.formatExample": "Contoh: {example}",
    "setup.profile.phone": "Telefon salun",
    "setup.profile.phone.hint": "Kod negara +{code}",
    "setup.profile.tax": "Cukai",
    "setup.profile.taxLabel": "Nama cukai",
    "setup.profile.taxRate": "Kadar (%)",
    "setup.profile.taxMode": "Harga",
    "setup.profile.taxInclusive": "Harga termasuk cukai",
    "setup.profile.taxExclusive": "Cukai ditambah semasa bayaran",
    "setup.profile.taxHint": "Kenakan cukai hanya jika perniagaan anda berdaftar. Tetapkan 0% jika tidak.",
    "setup.profile.defaultsApplied": "Tetapan lalai untuk {country} digunakan.",
    "setup.branch.title": "Lokasi dan waktu operasi anda",
    "setup.branch.sub": "Temujanji hanya boleh dibuat dalam waktu operasi. Anda boleh menambah cawangan lain dalam Tetapan.",
    "setup.branch.name": "Nama lokasi",
    "setup.branch.name.ph": "cth. Cawangan utama",
    "setup.branch.address": "Alamat",
    "setup.branch.phone": "Telefon",
    "setup.branch.hours": "Waktu operasi",
    "setup.branch.open": "Buka",
    "setup.branch.closed": "Tutup",
    "setup.branch.copyAll": "Salin Isnin ke semua hari buka",
    "setup.branch.to": "hingga",
    "setup.branch.invalid": "Waktu tutup mesti selepas waktu buka.",
    "setup.services.title": "Tambah perkhidmatan anda",
    "setup.services.sub": "Mula dengan templat dan laraskan harga — dipaparkan dalam {currency}.",
    "setup.services.templates": "Templat permulaan pantas",
    "setup.services.addTemplate": "Tambah {category}",
    "setup.services.added": "{n} perkhidmatan ditambah",
    "setup.services.empty": "Belum ada perkhidmatan. Pilih templat di atas atau tambah sendiri.",
    "setup.services.name": "Perkhidmatan",
    "setup.services.duration": "Minit",
    "setup.services.price": "Harga ({currency})",
    "setup.services.from": "Dari",
    "setup.services.addRow": "Tambah perkhidmatan",
    "setup.services.count": "{n} perkhidmatan",
    "setup.team.title": "Tambah pasukan anda",
    "setup.team.sub": "Tambah orang yang menerima temujanji atau menggunakan kaunter. E-mel adalah pilihan — tanpanya mereka hanya guna PIN kiosk.",
    "setup.team.you": "Anda",
    "setup.team.bookable": "Menerima temujanji",
    "setup.team.pin": "PIN staf",
    "setup.team.pin.hint": "4–6 digit untuk bertukar staf di tablet kaunter dikongsi dan kelulusan di kaunter.",
    "setup.team.pinSet": "PIN ditetapkan",
    "setup.team.noPin": "Tiada PIN",
    "setup.team.role": "Peranan",
    "setup.team.email.hint": "Kami akan menghantar jemputan untuk menetapkan kata laluan.",
    "setup.team.add": "Tambah ahli pasukan",
    "setup.team.invited": "Jemputan dihantar",
    "setup.team.noLogin": "Kiosk sahaja",
    "setup.team.empty": "Hanya anda buat masa ini? Tidak mengapa — anda boleh menambah ahli bila-bila masa.",
    "setup.team.clearPin": "Buang PIN",
    "setup.payments.title": "Bagaimana pelanggan akan membayar?",
    "setup.payments.sub": "Tunai dan terminal kad sedia ada sentiasa boleh digunakan. Bayaran kad dalam talian dan dalam aplikasi masuk ke akaun Stripe anda sendiri.",
    "setup.payments.stripe.title": "Sambungkan akaun Stripe anda sendiri",
    "setup.payments.stripe.body": "Wang masuk terus ke baki Stripe dan bank anda — platform tidak pernah memegang dana anda. Stripe mengenakan yuran pemprosesannya sendiri.",
    "setup.payments.stripe.cta": "Saya akan sambungkan Stripe",
    "setup.payments.stripe.tutorial": "Cara mencipta dan menyambung akaun Stripe",
    "setup.payments.stripe.later": "Sambungan dibuka dalam Tetapan → Bayaran apabila tersedia. Sementara itu anda boleh menerima tunai dan terminal kad.",
    "setup.payments.manual.title": "Tunai & manual sahaja buat masa ini",
    "setup.payments.manual.body": "Rekod tunai, terminal kad sendiri, pindahan bank atau e-dompet sebagai bayaran manual. Sambung Stripe bila-bila masa kemudian.",
    "setup.payments.manual.cta": "Guna tunai & manual",
    "setup.payments.chosen": "Dipilih",
    "setup.review.title": "Sedia untuk membuka salun anda",
    "setup.review.sub": "Semak ringkasan. Anda boleh mengubah semuanya kemudian dalam Tetapan.",
    "setup.review.profile": "{currency} · {timezone} · {tax}",
    "setup.review.hours": "{days} hari seminggu",
    "setup.review.noHours": "Tiada waktu operasi",
    "setup.review.services": "{n} perkhidmatan",
    "setup.review.team": "{n} orang",
    "setup.review.payments.manual": "Bayaran tunai & manual",
    "setup.review.payments.stripe": "Stripe (sambung dalam Tetapan)",
    "setup.review.payments.undecided": "Belum dipilih",
    "setup.review.skipped": "Dilangkau",
    "setup.review.missing": "Wajib",
    "setup.review.verifyFirst": "Sahkan e-mel anda ({email}) untuk meneruskan.",
    "setup.review.resend": "Hantar semula e-mel pengesahan",
    "setup.review.checkAgain": "Saya sudah mengesahkannya",
    "setup.review.plan": "Pelan Asas · USD 20/bulan · percubaan",
    "setup.review.finish": "Pergi ke papan pemuka",
    "setup.review.finishing": "Membuka salun anda…",
    "setup.done.toast": "Salun anda sudah sedia. Selamat datang!",
    "setup.error.setup_incomplete": "Sila lengkapkan langkah wajib dahulu.",
    "setup.error.email_unverified": "Sahkan alamat e-mel anda untuk meneruskan.",
    "setup.error.currency_locked": "Mata wang tidak boleh diubah selepas jualan direkodkan.",
    "setup.notOwner": "Salun anda masih disediakan oleh pemilik.",
  },
});
